'use client';

import { CheckmarkCircleIcon } from '@app/app/_icons/CheckmarkCircleIcon';
import { CloseCircleIcon } from '@app/app/_icons/CloseCircleIcon';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { ExclamationCircleIcon } from '@app/app/_icons/ExclamationCircleIcon';
import { InfoCircleIcon } from '@app/app/_icons/InfoCircleIcon';
import { ReactNode, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface Props {
    className?: string;
    containerClassName?: string;
    variant?: 'danger' | 'warning' | 'info' | 'hint' | 'success';
    canClose?: boolean;
    addon?: ReactNode;
    children: ReactNode;
}

export function Message({ className, containerClassName, variant = 'danger', canClose, addon, children }: Props) {
    const [open, setOpen] = useState(true);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    if (!open) {
        return null;
    }

    return (
        <div
            role="alert"
            className={twMerge(
                'flex flex-col gap-3 rounded-lg px-4 py-3 @container',
                variant === 'danger' && 'border border-red-400 bg-red-100 font-medium',
                variant === 'warning' && 'border border-orange-300 bg-orange-100 font-medium',
                variant === 'info' && 'border border-primary-700 bg-white font-medium',
                variant === 'hint' && 'bg-neutral-50 text-sm shadow-sm',
                variant === 'success' && 'border border-primary-350 bg-primary-100 text-sm shadow-sm',
                className,
            )}>
            <div className={twMerge('flex gap-3', containerClassName)}>
                {variant === 'danger' && <CloseCircleIcon className="size-6 shrink-0 text-red-500" />}
                {variant === 'warning' && <ExclamationCircleIcon className="size-6 shrink-0 text-orange-500" />}
                {variant === 'info' && <InfoCircleIcon className="size-6 shrink-0 text-primary-700" />}
                {variant === 'hint' && <InfoCircleIcon className="my-1 size-4 shrink-0 text-primary-700" />}
                {variant === 'success' && <CheckmarkCircleIcon className="my-1 size-4 shrink-0 text-primary-400" />}
                <div className="grow">{children}</div>
                {addon != null && <div className="hidden flex-col md:flex">{addon}</div>}
                {canClose && (
                    <button className="shrink-0 p-1" type="button" onClick={handleClose}>
                        <CloseIcon className="size-4" />
                    </button>
                )}
            </div>
            {addon != null && <div className="flex flex-col md:hidden">{addon}</div>}
        </div>
    );
}
